import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  FileText, 
  Edit3,
  Save,
  X,
  Send,
  Eye,
  Loader2,
  CheckCircle2,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  reminder: "bg-amber-50 text-amber-700",
  transactional: "bg-emerald-50 text-emerald-700",
  followup: "bg-blue-50 text-blue-700",
  custom: "bg-purple-50 text-purple-700"
};

const TemplatesSection = ({ password }) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [testEmailDialog, setTestEmailDialog] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [isSending, setIsSending] = useState(false);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/templates?password=${password}`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Kon templates niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [password]);

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await axios.put(
        `${API_URL}/admin/templates/${editingTemplate.id}?password=${password}`,
        {
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          html_content: editingTemplate.html_content
        }
      );
      toast.success("Template opgeslagen");
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Kon template niet opslaan");
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailDialog || !testEmail) return;
    
    setIsSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/templates/${testEmailDialog.id}/test?password=${password}&to_email=${encodeURIComponent(testEmail)}`
      );
      
      if (response.data.status === "sent") {
        toast.success(`Test email verzonden naar ${testEmail}`);
        setTestEmailDialog(null);
        setTestEmail("");
      } else {
        toast.error("Email verzenden mislukt");
      }
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error("Kon test email niet verzenden");
    } finally {
      setIsSending(false);
    }
  };

  const renderPreview = (htmlContent) => {
    // Replace template variables with example values
    let preview = htmlContent
      .replace(/\{\{name\}\}/g, "Jan de Vries")
      .replace(/\{\{score\}\}/g, "45")
      .replace(/\{\{outcome_label\}\}/g, "Twijfelgeval")
      .replace(/\{\{outcome_color\}\}/g, "#F59E0B")
      .replace(/\{\{result_url\}\}/g, "#")
      .replace(/\{\{report_url\}\}/g, "#")
      .replace(/\{\{date\}\}/g, new Date().toLocaleDateString("nl-NL"))
      .replace(/\{\{year\}\}/g, new Date().getFullYear().toString())
      .replace(/\{\{unsubscribe_url\}\}/g, "#");
    
    return preview;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Templates Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {templates.map((template) => (
          <motion.div
            key={template.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-zinc-200/60 rounded-xl p-5"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-medium text-zinc-900">{template.name}</h3>
                <p className="text-sm text-zinc-500 mt-1">{template.description}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryColors[template.category] || categoryColors.custom}`}>
                {template.category}
              </span>
            </div>

            <div className="bg-zinc-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-zinc-500 mb-1">Onderwerp:</p>
              <p className="text-sm text-zinc-700 font-medium">{template.subject}</p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewTemplate(template)}
                className="rounded-lg flex-1"
              >
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingTemplate({...template})}
                className="rounded-lg flex-1"
              >
                <Edit3 className="w-4 h-4 mr-1" />
                Bewerken
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTestEmailDialog(template)}
                className="rounded-lg"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Bewerken</DialogTitle>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Naam</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Onderwerp</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Variabelen: {"{{name}}"}, {"{{score}}"}, {"{{outcome_label}}"}
                </p>
              </div>
              
              <div>
                <Label>HTML Content</Label>
                <Textarea
                  value={editingTemplate.html_content}
                  onChange={(e) => setEditingTemplate({...editingTemplate, html_content: e.target.value})}
                  className="mt-1 font-mono text-xs h-[300px]"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Variabelen: {"{{name}}"}, {"{{score}}"}, {"{{outcome_label}}"}, {"{{result_url}}"}, {"{{report_url}}"}, {"{{year}}"}, {"{{unsubscribe_url}}"}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveTemplate} className="bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]">
              <Save className="w-4 h-4 mr-2" />
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="bg-zinc-100 rounded-lg p-4 overflow-auto max-h-[60vh]">
              <div 
                dangerouslySetInnerHTML={{ __html: renderPreview(previewTemplate.html_content) }}
                className="bg-white"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
              Sluiten
            </Button>
            <Button 
              onClick={() => {
                setTestEmailDialog(previewTemplate);
                setPreviewTemplate(null);
              }}
              className="bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
            >
              <Send className="w-4 h-4 mr-2" />
              Test Email Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={!!testEmailDialog} onOpenChange={() => setTestEmailDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Email Versturen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Verstuur een test email met template "{testEmailDialog?.name}".
            </p>
            
            <div>
              <Label>Email adres</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialog(null)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSendTestEmail}
              disabled={!testEmail || isSending}
              className="bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesSection;
